import { useMediaQuery, useOrientation } from '@uidotdev/usehooks'
import { useMemo } from 'react'

export interface MobileDetectionResult {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  orientation: 'portrait' | 'landscape'
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  hasRestrictions: boolean
}

/**
 * Custom hook to detect mobile devices and apply restrictions
 *
 * Breakpoints:
 * - Mobile: < 768px (restricted)
 * - Tablet: 768px - 1024px (no restrictions)
 * - Desktop: > 1024px (no restrictions)
 *
 * @returns MobileDetectionResult object with device information and restriction status
 */
export const useMobileDetection = (): MobileDetectionResult => {
  // Media queries for different screen sizes
  const isXs = useMediaQuery('(max-width: 575px)')
  const isSm = useMediaQuery('(min-width: 576px) and (max-width: 767px)')
  const isMd = useMediaQuery('(min-width: 768px) and (max-width: 991px)')
  const isLg = useMediaQuery('(min-width: 992px) and (max-width: 1199px)')

  // Mobile detection (phones only)
  const isMobilePortrait = useMediaQuery(
    '(max-width: 767px) and (orientation: portrait)'
  )
  const isMobileLandscape = useMediaQuery(
    '(max-width: 767px) and (orientation: landscape)'
  )

  // Tablet detection
  const isTabletPortrait = useMediaQuery(
    '(min-width: 768px) and (max-width: 1024px) and (orientation: portrait)'
  )
  const isTabletLandscape = useMediaQuery(
    '(min-width: 768px) and (max-width: 1024px) and (orientation: landscape)'
  )

  // Get current orientation
  const orientation = useOrientation()

  // Additional checks for edge cases
  const hasTouch = useMediaQuery('(pointer: coarse)')
  const isSmallHeight = useMediaQuery('(max-height: 600px)')

  return useMemo(() => {
    // Determine screen size
    let screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'xl'
    if (isXs) {
      screenSize = 'xs'
    } else if (isSm) {
      screenSize = 'sm'
    } else if (isMd) {
      screenSize = 'md'
    } else if (isLg) {
      screenSize = 'lg'
    }

    // Mobile detection logic
    const isMobile =
      isMobilePortrait ||
      isMobileLandscape ||
      screenSize === 'xs' ||
      screenSize === 'sm'

    // Tablet detection logic
    const isTablet =
      isTabletPortrait || isTabletLandscape || (screenSize === 'md' && hasTouch)

    // Desktop detection
    const isDesktop = !(isMobile || isTablet)

    // Handle edge cases for landscape phones that might be detected as tablets
    const isLandscapePhone =
      orientation?.type === 'landscape-primary' &&
      isSmallHeight &&
      (screenSize === 'xs' || screenSize === 'sm')

    // Final mobile determination including edge cases
    const finalIsMobile = isMobile || isLandscapePhone
    const finalIsTablet = isTablet && !isLandscapePhone

    // Restrictions apply only to mobile devices (phones)
    // Tablets and desktops are restriction-free
    const hasRestrictions = finalIsMobile

    return {
      isMobile: finalIsMobile,
      isTablet: finalIsTablet,
      isDesktop: isDesktop,
      orientation: orientation?.type?.includes('landscape')
        ? 'landscape'
        : 'portrait',
      screenSize,
      hasRestrictions
    }
  }, [
    isXs,
    isSm,
    isMd,
    isLg,
    isMobilePortrait,
    isMobileLandscape,
    isTabletPortrait,
    isTabletLandscape,
    orientation?.type,
    hasTouch,
    isSmallHeight
  ])
}
